import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import undosvg from './assets/undo.svg';
import redosvg from './assets/redo.svg';
import 'bootstrap/dist/css/bootstrap.min.css';
import ChapterSection, { NewThumbnail } from './ChapterSection';
import BottomSidebar from './BottomSidebar';
import zoomout from './assets/zoomout.svg';
import zoomin from './assets/zoomin.svg';
import './ChapterEditorMain.css';
import SproutVideo from '../components/sprout-video';
import { EslidesEvent, Presentation, ScheduleEndpointResponseObject, Version } from "../types/presentation";
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { useUndoRedo } from './CustomHook/useUndoRedo';
import { useVideoContext } from './Context/VideoContext';
import UserContext from "../contexts/user";

// Define SproutVideo type interfaces
interface SproutVideoPlayer {
  bind: (event: string, callback: (e: any) => void) => void;
  unbind: (event: string) => void;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
}

interface SproutVideoStatic {
  Player: new (options: { videoId: string }) => SproutVideoPlayer;
}

// Extend Window interface to include SV property
declare global {
  interface Window {
    SV?: SproutVideoStatic;
  }
}

export type Thumbnail = {
  id: string;
  time: number
};

export type PreSignedUrl = {
  id: number;
  signedUrl: string;
  time: number;
  duration?: number;
  width?: number;
};

export type ThumbnailUrls = {
  data: {
    signedUrl: string
  }
}

interface LocationState {
  version?: Version;
}

interface ThumbnailEndTime {
  time: number;      // Start time (or time in seconds)
  id: number;        // Unique identifier
  endTime: number;   // End time (likely in seconds)
}

// Add ChapterData interface
interface ChapterData {
  id: number;
  url: string;
  time: number;
  duration: number;
  width: number;
}

export type ImageActionState = {
  images: string[]; // All available images
  selected: number[]; // Indexes of selected images
  removedImages: { index: number; image: string }[]; // For cut/merge removed images
  operation?: 'merge' | 'cut' | 'resize' | null; // Type of operation
  mergedChapter?: ChapterData; // For merge operation
  originalChapters?: ChapterData[]; // For merge operation
  cutPosition?: number; // For cut operation
  originalChapter?: ChapterData; // For cut operation
  splitChapters?: ChapterData[]; // For cut operation
  chapters: ChapterData[];
  resizeOperation?: { // For resize operation
    beforeState: { chapters: any };
    afterState: { chapters: any };
    nextChapter?: number;
    type: string;
    affectedChapters: number[];
  };
};

const ChapterEditorMain: React.FC<{
  locale: string;
  setLocale: any;
}> = ({ locale, setLocale }) => {
  const { event_slug, presentation_id } = useParams<{
    event_slug: string;
    presentation_id: string;
  }>();

  const { registerVideoPlayers } = useVideoContext();
  const userContext = useContext(UserContext);

  const [v, setV] = useState<SproutVideoPlayer | null>(null);
  const [w, setW] = useState<SproutVideoPlayer | null>(null);
  const [played, setPlayed] = useState(false);
  const [singleVersion, setSingleVersion] = useState<Version | null>(null);

  const queryClient = useQueryClient();
  const [fetchData, setFetchData] = useState(false);
  const [imageUrls, setImageUrls] = useState<Thumbnail[]>([]);
  const [time, setTime] = useState("");
  const [signedUrls, setSignedUrls] = useState<PreSignedUrl[]>([]);
  // const [chapters, setChapters] = useState<ChapterData[]>([]);
  const location = useLocation();
  const state = location.state as LocationState;
  const version = state?.version;
  const [dataFromChild, setDataFromChild] = useState<string>('');
  const [hasModification, setHasModification] = useState(false);
  const { duration, isPlaying, seek } = useVideoContext();
  const [updateStatus, setUpdateStatus] = useState<Boolean>(false)
  useEffect(() => {
    if (version) {
      setSingleVersion(version);
    }
  }, [version]);

  const [zoom, setZoom] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [zoomMarginTop, setZoomMarginTop] = useState('11rem');
  const [videoHeight, setVideoHeight] = useState('28.063rem');
  const [webcamHeight, setWebcamHeight] = useState('9.313rem');
  const [videoWidth, setVideoWidth] = useState('805px');
  const [webcamWidth, setWebcamWidth] = useState('310px');
  const [details, setDetails] = useState<{
    [key: number]: {
      title: string;
      topic: string;
    }
  }>({});
  const [removedThumbnails, setRemovedThumbnails] = useState<string[]>([]);
  const [newThumbnails, setNewThumbnails] = useState<NewThumbnail[]>([]);
  const initialChaptersRef = useRef<ChapterData[]>([]);

  const basePixelsPerSecond = 18;
  // Scale pixels per second based on zoom level
  const pixelsPerSecond = useMemo(() => {
    return basePixelsPerSecond * (1 + (zoom / 100) * 4);
  }, [zoom]);

  // test data
  // const chaptersData = useMemo(() => {
  //   return Array.from({ length: 14 }, (_, index) => ({
  //     id: index,
  //     url: `https://dummyimage.com/300x200/000/fff&text=Thumbnail${index}`,
  //     time: index * 10,
  //     duration: 60,
  //     width: 300,
  //   }))
  // }, []);

  // Create chapters data - now with proper width calculation based on time differences
  const chaptersData = useMemo(() => {
    return signedUrls.map((thumbnail, index) => {
      const startTime = thumbnail.time;
      const endTime = index === signedUrls.length - 1
        ? duration * 1000
        : signedUrls[index + 1].time;

      const timeDifference = endTime - startTime;
      const width = (timeDifference / 1000) * pixelsPerSecond;

      return {
        id: index,
        url: thumbnail.signedUrl,
        time: startTime,
        duration: timeDifference / 1000,
        width: width,
      };
    });
  }, [signedUrls, pixelsPerSecond, duration]);


  const {
    state: states,
    setState,
    undo,
    redo,
    canUndo,
    canRedo,
    updateInitialChapters,
  } = useUndoRedo<ImageActionState>({
    images: chaptersData.map(ch => ch.url),
    selected: [],
    removedImages: [],
    chapters: chaptersData || []
  });

  useEffect(() => {
    if (chaptersData.length > 0 && initialChaptersRef.current.length === 0) {
      // Only update initial chapters once
      initialChaptersRef.current = chaptersData;
      updateInitialChapters(chaptersData);

      // Set initial state
      setState({
        images: chaptersData.map(ch => ch.url),
        selected: [],
        removedImages: [],
        chapters: chaptersData,
        operation: null
      });
    }
  }, [chaptersData, updateInitialChapters]);

  useEffect(() => {
    const updateZoomMargin = () => {
      if (dataFromChild && dataFromChild !== '') {
        setZoomMarginTop(
          window.innerWidth > 2560 ? '-15px' :
            window.innerWidth >= 1920 ? '-6px' :
              window.innerWidth > 1600 ? '-5px' :
                window.innerWidth > 1366 ? '-3px' :
                  window.innerWidth > 1024 ? '15px' : '10px'
        );
      } else {
        setZoomMarginTop(
          window.innerWidth > 2560 ? '13rem' :
            window.innerWidth > 1920 ? '11rem' :
              window.innerWidth > 1600 ? '8rem' :
                window.innerWidth > 1366 ? '6rem' :
                  window.innerWidth <= 433 ? '25rem' :
                    window.innerWidth > 1024 ? '4rem' : '3rem'
        );
      }
    };

    const updateVideoHeight = () => {
      if (dataFromChild === 'vertical') {
        setVideoHeight(
          window.innerWidth > 2560 ? '17rem' :
            window.innerWidth > 1920 ? '10rem' :
              window.innerWidth > 1600 ? '12rem' :
                window.innerWidth > 1366 ? '12rem' :
                  window.innerWidth > 1024 ? '8rem' : '6rem'
        );
      } else if (dataFromChild && dataFromChild !== '') {
        setVideoHeight(
          window.innerWidth > 2560 ? '17.813rem' :
            window.innerWidth > 1920 ? '10.813rem' :
              window.innerWidth > 1600 ? '17.2rem' :
                window.innerWidth > 1366 ? '15rem' :
                  window.innerWidth > 1024 ? '9rem' : '7rem'
        );
      } else {
        setVideoHeight(
          window.innerWidth > 2560 ? '32.063rem' :
            window.innerWidth > 1920 ? '22.063rem' :
              window.innerWidth > 1600 ? '24rem' :
                window.innerWidth > 1366 ? '26rem' :
                  window.innerWidth > 1024 ? '16rem' : '12rem'
        );
      }
    };

    const updateWebcamHeight = () => {
      if (dataFromChild && dataFromChild !== '') {
        setWebcamHeight(
          window.innerWidth > 2560 ? '12.313rem' :
            window.innerWidth > 1920 ? '8.313rem' :
              window.innerWidth > 1600 ? '8rem' :
                window.innerWidth > 1366 ? '9rem' :
                  window.innerWidth > 1024 ? '6rem' : '5rem'
        );
      } else if (dataFromChild === 'vertical') {
        setWebcamHeight(
          window.innerWidth > 2560 ? '12.313rem' :
            window.innerWidth > 1920 ? '10.313rem' :
              window.innerWidth > 1600 ? '8rem' :
                window.innerWidth > 1366 ? '9rem' :
                  window.innerWidth > 1024 ? '6rem' : '5rem'
        );
      } else {
        setWebcamHeight(
          window.innerWidth > 2560 ? '203px' :
            window.innerWidth > 1920 ? '170px' :
              window.innerWidth > 1600 ? '160px' :
                window.innerWidth > 1366 ? '180px' :
                  window.innerWidth > 1024 ? '120px' : '100px'
        );
      }
    };

    const updateVideoWidth = () => {
      if (dataFromChild === 'vertical') {
        setVideoWidth(
          window.innerWidth > 2560 ? '805px' :
            window.innerWidth > 1920 ? '705px' :
              window.innerWidth > 1600 ? '505px' :
                window.innerWidth > 1366 ? '405px' :
                  window.innerWidth > 1024 ? '405px' : '305px'
        );
      } else if (dataFromChild && dataFromChild !== '') {
        setVideoWidth(
          window.innerWidth > 2560 ? '805px' :
            window.innerWidth > 1920 ? '705px' :
              window.innerWidth > 1600 ? '605px' :
                window.innerWidth > 1366 ? '505px' :
                  window.innerWidth > 1024 ? '405px' : '305px'
        );
      } else {
        setVideoWidth(
          window.innerWidth > 2560 ? '805px' :
            window.innerWidth > 1920 ? '805px' :
              window.innerWidth > 1600 ? '805px' :
                window.innerWidth > 1366 ? '805px' :
                  window.innerWidth > 1024 ? '405px' : '305px'
        );
      }
    };

    const updateWebcamWidth = () => {
      if (dataFromChild && dataFromChild !== '') {
        setWebcamWidth(
          window.innerWidth > 2560 ? '310px' :
            window.innerWidth > 1920 ? '280px' :
              window.innerWidth > 1600 ? '260px' :
                window.innerWidth > 1366 ? '240px' :
                  window.innerWidth > 1024 ? '220px' : '200px'
        );
      } else if (dataFromChild === 'vertical') {
        setWebcamWidth(
          window.innerWidth > 2560 ? '310px' :
            window.innerWidth > 1920 ? '280px' :
              window.innerWidth > 1600 ? '260px' :
                window.innerWidth > 1366 ? '240px' :
                  window.innerWidth > 1024 ? '220px' : '200px'
        );
      } else {
        setWebcamWidth(
          window.innerWidth > 2560 ? '310px' :
            window.innerWidth > 1920 ? '280px' :
              window.innerWidth > 1600 ? '260px' :
                window.innerWidth > 1366 ? '310px' :
                  window.innerWidth > 1024 ? '220px' : '200px'
        );
      }
    };

    // Initial calculations
    updateZoomMargin();
    updateVideoHeight();
    updateWebcamHeight();
    updateVideoWidth();
    updateWebcamWidth();

    // Add event listeners
    window.addEventListener('resize', updateZoomMargin);
    window.addEventListener('resize', updateVideoHeight);
    window.addEventListener('resize', updateWebcamHeight);
    window.addEventListener('resize', updateVideoWidth);
    window.addEventListener('resize', updateWebcamWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateZoomMargin);
      window.removeEventListener('resize', updateVideoHeight);
      window.removeEventListener('resize', updateWebcamHeight);
      window.removeEventListener('resize', updateVideoWidth);
      window.removeEventListener('resize', updateWebcamWidth);
    };
  }, [dataFromChild]);

  const handleSliderClick = (e: React.MouseEvent) => {
    try {

      const slider = sliderRef.current;
      if (!slider) return;

      const rect = slider.getBoundingClientRect();
      const clickPosition = e.clientX - rect.left;
      const newZoom = Math.min(Math.max((clickPosition / rect.width) * 100, 0), 100);
      setZoom(newZoom);
    } catch (error) {
      console.error(error);
    }
  };

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    try {

      const startX = e.clientX;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const slider = sliderRef.current;
        if (!slider) return;

        const rect = slider.getBoundingClientRect();
        const deltaX = moveEvent.clientX - startX;
        const newZoom = Math.min(Math.max(((deltaX + (zoom / 100) * rect.width) / rect.width) * 100, 0), 100);
        setZoom(newZoom);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } catch (error) {
      console.error(error);
    }
  };

  const { data, isLoading, error } = useQuery(
    "event",
    async () => {
      const response = await axios.get<ScheduleEndpointResponseObject>(
        `${process.env.REACT_APP_API_ENDPOINT}/api/public/schedule/${event_slug}`
      );
      return response.data;
    },
    {
      enabled: fetchData,
      onSuccess: (resp) => {
        const { event } = resp.data;
        resp.data.presentations.forEach((presentation) => {
          queryClient.setQueryData(["presentation", presentation._id], () => ({
            statusCode: 201,
            message: "event.fetch-success",
            data: { event, presentation },
          }));
        });
      },
    }
  );

  useEffect(() => {
    if (!data) {
      setFetchData(true);
    }
  }, [setFetchData, data, presentation_id]);

  const datas = data?.data.presentations;

  const security_token: string = version?.video?.security_token ?? "";
  const id: string = version?.video?.id ?? "";
  const thumbnails: Thumbnail[] = version?.thumbnails ?? [];
  const webcamId: string = version?.webcam?.id ?? "";
  const webcamToken: string = version?.webcam?.security_token ?? "";
  const skip: number = datas?.[0]?.skip ?? 0;

  const speaker_info = {
    title: datas?.[0]?.speaker_info?.title ?? "",
    firstName: datas?.[0]?.speaker_info?.firstName ?? "",
    lastName: datas?.[0]?.speaker_info?.lastName ?? "",
    picture: datas?.[0]?.speaker_info?.picture ?? "",
    speaker_id: datas?.[0]?.speaker_info?._id ?? "",
    presentation_name: datas?.[0]?.name ?? "",
    start_time: datas?.[0]?.time ?? "",
    end_time: datas?.[0]?.end_time ?? "",
    date: datas?.[0]?.date ?? "",
    day: datas?.[0]?.day ?? "",
    event_slug: event_slug,
    presentation_id: presentation_id
  };


  useEffect(() => {
    const fetchThumbnailUrls = async () => {
      try {
        const urls = await Promise.all(
          thumbnails.map(async (thumbnail) => {
            try {
              const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/api/s3/images`,
                { key: thumbnail.id },
                { headers: { 'Content-Type': 'application/json' } }
              );
              return {
                id: parseInt(thumbnail.id),
                signedUrl: response.data.data.signedUrl,
                time: thumbnail.time
              };
            } catch (error) {
              console.error(`Failed to fetch signed URL for key: ${thumbnail.id}`, error);
              return null;
            }
          })
        );
        setSignedUrls(urls.filter((item) => item !== null) as PreSignedUrl[]);
      } catch (error) {
        console.error('Error fetching thumbnail URLs:', error);
      }
    };

    if (signedUrls.length === 0 && thumbnails.length > 0) {
      fetchThumbnailUrls();
    }
  }, [thumbnails, signedUrls]);

  useEffect(() => {
    if (!singleVersion) {
      return;
    }

    let video: SproutVideoPlayer | null = null;
    let webcam: SproutVideoPlayer | null = null;
    console.log(singleVersion, "singleVersion");
    
    if (singleVersion.video && singleVersion.video.id && window.SV) {
      try {
        video = new window.SV.Player({ videoId: singleVersion.video.id });
        console.log("video", video);
        
        if (v === null) {
          setV(video);
        }
      } catch (error) {
        console.error("Error initializing main video player:", error);
      }
    }

    if (singleVersion.webcam && singleVersion.webcam.id && window.SV) {
      try {
        webcam = new window.SV.Player({ videoId: singleVersion.webcam.id });
        if (w === null) {
          setW(webcam);
        }
      } catch (error) {
        console.error("Error initializing webcam player:", error);
      }
    }

    registerVideoPlayers(video, webcam);

    if (video) {
      video.bind("ready", (e: any) => {
      });

      video.bind("play", (e: any) => {
        setPlayed(true);

        if (webcam) {
          webcam.play();
        }
      });
    }

    if (webcam) {
      const safeWebcam = webcam;
      webcam.bind("progress", (e: any) => {
        if (video) {
          const videoTime = video.getCurrentTime();
          const webcamTime = safeWebcam.getCurrentTime();

          if (videoTime + 0.5 < webcamTime || videoTime - 0.5 > webcamTime) {
            video.seek(e.data.time);
          }
        }
      });
    }

    return () => {
      if (video) {
        video.unbind("ready");
        video.unbind("play");
      }

      if (webcam) {
        webcam.unbind("progress");
      }
    };
  }, [singleVersion, v, w, registerVideoPlayers]);

  const handleDataFromChild = (data: string | null) => {
    setDataFromChild(data || '');
  }

  const thumbnailEndTimes: ThumbnailEndTime[] = signedUrls.map((item, index) => {
    const timeInSeconds = item.time / 1000; // Assuming time is in milliseconds
    const nextTimeInSeconds = index < signedUrls.length - 1
      ? signedUrls[index + 1].time / 1000
      : duration;

    return {
      time: timeInSeconds,
      id: index,
      endTime: nextTimeInSeconds,
    };
  });

  const [videoHeightMobile, setVideoHeightMobile] = useState('200px');

  useEffect(() => {
    const calculateMobileHeight = () => {
      const aspectRatio = 16 / 9; // Adjust to your video's aspect ratio
      const mobileWidth = window.innerWidth - 32; // 2rem margin
      setVideoHeightMobile(`${mobileWidth / aspectRatio}px`);
    };

    calculateMobileHeight();
    window.addEventListener('resize', calculateMobileHeight);
    return () => window.removeEventListener('resize', calculateMobileHeight);
  }, []);

  return (
    <>
      <div className='d-block d-lg-none p-2 bg-white'>
        <img
          src='/images/logo-default.png'
          alt="Event Logo"
          style={{
            height: '50%',
            width: 'auto',
          }}
          className="img-fluid bg-white"
        />
      </div>
      <div className="d-flex flex-column overflow-hidden position-relative min-vh-100 position-md-static d-md-block d-md-none" style={{ top: 0, backgroundColor: '#1E2024' }}>
        <div className="text-white p-4 position-relative d-flex flex-column">
          {/* Top section with video and webcam */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start position-relative" style={{ zIndex: 2 }}>
            <div className="d-flex align-items-center p-1" style={{
              top: '30px',
              left: '73px',
              height: '40px',
              width: '80px',
              opacity: 1,
              zIndex: 3
            }}>
              {/* Undo/Redo buttons */}
              <button
                className={`${hasModification ? "undo-redo-btn" : ''} p-0 d-flex align-items-center justify-content-center`}
                style={{
                  transition: 'transform 0.1s, background-color 0.3s, filter 0.3s',
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#4b4f5a',
                  border: 'none',
                  borderRadius: '5px 0 0 5px',
                  cursor: (!hasModification || !canUndo) ? 'auto' : 'pointer',
                  opacity: (!hasModification || !canUndo) ? 0.5 : 1
                }}
                onClick={undo}
                disabled={!hasModification || !canUndo}
              >
                <img src={undosvg} alt="Undo" className='border-right-thin' style={{ width: '40px', height: '44px' }} />
              </button>
              <button
                className={`${hasModification ? "undo-redo-btn" : ''} p-0 d-flex align-items-center justify-content-center`}
                style={{
                  transition: 'transform 0.1s, background-color 0.3s, filter 0.3s',
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#4b4f5a',
                  border: 'none',
                  borderRadius: '0 5px 5px 0',
                  cursor: (!hasModification || !canRedo) ? 'auto' : 'pointer',
                  opacity: (!hasModification || !canRedo) ? 0.5 : 1
                }}
                onClick={redo}
                disabled={!hasModification || !canRedo}
              >
                <img src={redosvg} alt="Redo" style={{ width: '40px', height: '44px' }} />
              </button>
            </div>

            {/* Main video container */}
            <div className="position-absolute" style={
              window.innerWidth <= 433 ?
                // Mobile styles with increased top margin
                {
                  top: '16rem', // Increased from 1rem to 3rem
                  width: 'calc(100% - 2rem)',
                  height: videoHeightMobile,
                  borderRadius: '5px',
                  zIndex: 2
                } :
                // Desktop styles (keep your existing logic)
                dataFromChild === 'vertical' ? {
                  top: '1.938rem',
                  right: '2rem',
                  width: videoWidth,
                  height: videoHeight,
                  borderRadius: '5px',
                  opacity: 1,
                  zIndex: 2
                } : dataFromChild && dataFromChild !== '' ? {
                  top: '1rem',
                  right: '1.5rem',
                  width: videoWidth,
                  height: videoHeight,
                  zIndex: 2
                } : {
                  right: '1rem',
                  width: videoWidth,
                  height: videoHeight,
                  borderRadius: '5px',
                  opacity: 1,
                  zIndex: 2
                }
            }>
              <div className='w-100 h-100' style={{ objectFit: 'cover' }}>
                {security_token && id ? (
                  <SproutVideo
                    security_token={security_token}
                    id={id}
                    skip={skip || 0}
                    className=""
                    bigPlayButton={true}
                    volume={1}
                    showcontrols={true}
                  />
                ) : (
                  <div className="text-center text-white p-3 bg-secondary h-100 d-flex align-items-center justify-content-center">
                    No video selected
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Webcam section */}
          <div className="row mt-4 gap-2 position-relative" style={{ zIndex: 2 }}>
            <div className="col-12 col-md-6">
              <div className="bg-secondary p-2" style={
                dataFromChild && dataFromChild != '' ? {
                  maxWidth: '300px',
                  width: webcamWidth,
                  height: webcamHeight,
                  zIndex: 2
                } : {
                  top: "91px",
                  left: "33px",
                  width: webcamWidth,
                  height: webcamHeight,
                  zIndex: 2
                }}>
                {webcamId && webcamToken ? (
                  <SproutVideo
                    security_token={webcamToken}
                    id={webcamId}
                    skip={skip || 0}
                    className=""
                    bigPlayButton={true}
                    volume={1}
                    showcontrols={true}
                  />
                ) : (
                  <div className="text-center text-white p-3 d-flex align-items-center justify-content-center h-100">
                    No webcam feed available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Zoom controls */}
          <div className="d-flex py-3 position-relative" style={{
            marginTop: zoomMarginTop,
            zIndex: 2
          }}>
            <div className="slider d-flex align-items-center rounded-pill" >
              <img src={zoomout} width={18} height={18} alt="Zoom out" className="mr-2" onClick={() => setZoom((prev) => Math.max(prev - 10, 0))} />
              <div ref={sliderRef} className="position-relative rounded-pill" style={{ width: '4rem', height: '4px', backgroundColor: '#4B4F5A', cursor: 'pointer' }} onMouseDown={(e) => handleSliderClick(e)}>
                <div ref={thumbRef} className="slider-bar position-absolute rounded" style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  left: `calc(${zoom}% - 10px)`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#24B2FF',
                  cursor: 'grab',
                }} onMouseDown={handleThumbMouseDown} />
              </div>
              <img src={zoomin} width={18} height={18} alt="Zoom in" className="ml-2" onClick={() => setZoom((prev) => Math.min(prev + 10, 100))} />
            </div>
          </div>

          {/* Chapter section - now with proper positioning */}
          <div className="position-relative" style={{ zIndex: 1, marginTop: '2rem' }}>
            <div ref={scrollRef} className="text-white px-4 py-3" style={{
              position: 'relative',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              whiteSpace: 'nowrap',
              zIndex: 1
            }}>
              {/* Chapter content */}
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-container">
        <ChapterSection
          zoom={zoom}
          setZoom={setZoom}
          signedUrls={signedUrls}
          // selectedChapters={selectedChapters}
          // setSelectedChapters={setSelectedChapters}
          selectedChapters={states}
          setSelectedChapters={setState}
          setSignedUrls={setSignedUrls}
          onDataFromThird={setDataFromChild}
          dataFromChild={dataFromChild}
          setHasModification={setHasModification}
          locale={locale}
          thumbnailEndTimes={thumbnailEndTimes}
          // chapters={chapters}
          // setChapters={setChapters}
          singleVersion={singleVersion}
          setDetails={setDetails}
          details={details}
          removedThumbnails={removedThumbnails}
          setRemovedThumbnails={setRemovedThumbnails}
          newThumbnails={newThumbnails}
          setNewThumbnails={setNewThumbnails}
          updateStatus={updateStatus}
        />

        <BottomSidebar
          speaker_info={speaker_info}
          locale={locale}
          setLocale={setLocale}
          hasModification={hasModification}
          setHasModification={setHasModification}
          version_id={singleVersion?.versionId || ''}
          // chapters={chapters}
          // setChapters={setChapters}
          setDetails={setDetails}
          details={details}
          signedUrl={signedUrls}
          removedThumbnails={removedThumbnails}
          setRemovedThumbnails={setRemovedThumbnails}
          newThumbnails={newThumbnails}
          setNewThumbnails={setNewThumbnails}
          selectedChapters={states}
          setSelectedChapters={setState}
          setUpdateStatus={setUpdateStatus}
          PresentationData={data?.data.presentations}
        />
      </div>

    </>
  );
};

export default ChapterEditorMain;